import React, { useCallback, useEffect, useState, useRef } from 'react';
import { connect } from 'react-redux';
import { 
    Box, 
    Button, 
    Grid,
    Fab,
    Tooltip,
    Typography
} from '@mui/material';
import {
    CallEnd as CallEndIcon,
    Cameraswitch as CameraswitchIcon,
    VolumeOff as VolumeOffIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

import {
    cancelCallRequestSuccess,
    finishCallRequestSuccess,
    finishCallRequest,
    refreshCallRequest
} from '../actions/callRequestActions';

import PurchaseOrder from './PurchaseOrder';
import InCallCart from './cart/InCallCart';
import QRProductButton from './QRProductButton';
import useQRDetection from '../hooks/useQRDetection';
import useFullCatalog from './showcase/useFullCatalog';
import ProductCarousel from './showcase/ProductCarousel';
import PinnedSpotlight from './showcase/PinnedSpotlight';
import ProductQuickDialog from './showcase/ProductQuickDialog';

import { toastr } from 'react-redux-toastr';

/* global OT */

const Call = ({
    callRequest,
    call,
    store,
    cancelCallRequestSuccess,
    finishCallRequestSuccess,
    finishCallRequest,
    refreshCallRequest
}) => {
    const navigate = useNavigate();
    const [audioBlocked, setAudioBlocked] = useState(false);
    const [selectedProduct, setSelectedProduct] = useState(null);
    const sessionRef = useRef(null);
    const publisherRef = useRef(null);
    const subscriberRef = useRef(null);
    const refreshCallRequestRef = useRef(refreshCallRequest);
    refreshCallRequestRef.current = refreshCallRequest;

    // Carrusel con el catálogo de la tienda. Lo que la tienda muestra ahora
    // (pinnedProduct / pinnedUrl) llega con el poll del call request (App.js,
    // cada 5 s) y, al instante, con la señal 'pin' de OpenTok.
    const storeId = callRequest && callRequest.storeId;
    const catalog = useFullCatalog(storeId);
    const pinnedProduct = (callRequest && callRequest.pinnedProduct) || null;
    const pinnedUrl = (!pinnedProduct && callRequest && callRequest.pinnedUrl) || null;
    const purchaseOrderCount =
        (callRequest && callRequest.purchaseOrders && callRequest.purchaseOrders.length) || 0;

    const { url: qrUrl, dismiss: dismissQrUrl } = useQRDetection({
        containerId: 'subscriber',
        enabled: Boolean(call),
    });

    // Check no call EFFECT
    useEffect(() => {
        if (!call) {
            navigate('/home');
        }
    }, [call, navigate]);

    // Check no callRequest EFFECT
    useEffect(() => {
        if (!callRequest) {
            navigate('/home');
        }
    }, [callRequest, navigate]);

    // Check State EFFECT
    useEffect(() => {
        console.log(
            '::: Call CHECK STATE EFFECT',
            callRequest
        );

        if (callRequest && callRequest.state === 'CANCELLED') {
            cancelCallRequestSuccess();
            toastr.info('Info', 'Su llamada ha sido cancelada.');
        }

        if (callRequest && callRequest.state === 'FINISHED') {
            finishCallRequestSuccess();
            toastr.info('Info', 'Su llamada ha sido finalizada.');
        }
    }, [callRequest, cancelCallRequestSuccess, finishCallRequestSuccess]);

    // Initialize OpenTok session
    useEffect(() => {
        if (!call || !call.tokboxApiKey || !call.tokboxSessionId || !call.tokboxTokenCallRequest) {
            return;
        }

        let cancelled = false;

        const initSession = () => {
            if (cancelled) return;

            // No loguear API key, session id ni token: quedan visibles en la consola del navegador.

            const session = OT.initSession(call.tokboxApiKey, call.tokboxSessionId);
            sessionRef.current = session;

            const handleError = (error) => {
                if (error) {
                    console.error('OpenTok Error:', error);
                    toastr.error('Error', error.message || 'Error en la conexión de video');
                }
            };

            session.on('streamCreated', function(event) {
                const subscriber = session.subscribe(event.stream, 'subscriber', {
                    insertMode: 'append',
                    width: '100%',
                    height: '100%',
                    subscribeToAudio: true
                }, handleError);
                subscriberRef.current = subscriber;

                // Browsers may block audio autoplay; OT fires audioBlocked when
                // that happens and silences the <video> element. Without this
                // handler the user has to click OT's default speaker icon twice
                // (first click only re-syncs OT's internal state, second click
                // lands in a fresh user-gesture context and finally plays).
                subscriber.on('audioBlocked', () => {
                    setAudioBlocked(true);
                });
                subscriber.on('audioUnblocked', () => {
                    setAudioBlocked(false);
                });
            });

            // el operador cambió lo que muestra: no esperar al próximo poll
            session.on('signal:pin', function() {
                refreshCallRequestRef.current();
            });

            session.on('sessionDisconnected', function(event) {
                console.log('::: Session disconnected:', event);
                toastr.info('Info', 'Sesión desconectada');
            });

            const publisher = OT.initPublisher('publisher', {
                insertMode: 'append',
                width: '100%',
                height: '100%'
            }, handleError);
            publisherRef.current = publisher;

            session.connect(call.tokboxTokenCallRequest, function(error) {
                if (error) {
                    handleError(error);
                } else {
                    console.log('::: Connected to session');
                    session.publish(publisher, handleError);
                }
            });
        };

        const reportLoadFailure = () => {
            if (cancelled) return;
            console.error('OpenTok library not loaded');
            toastr.error('Error', 'OpenTok library not loaded');
        };

        if (typeof OT !== 'undefined') {
            initSession();
        } else {
            // The static <script> in index.html failed to load on this device
            // (flaky mobile network, ad-blocker, etc.). Try injecting it again
            // before giving up.
            const script = document.createElement('script');
            script.src = 'https://static.opentok.com/v2/js/opentok.min.js';
            script.async = true;
            script.onload = () => {
                if (typeof OT !== 'undefined') initSession();
                else reportLoadFailure();
            };
            script.onerror = reportLoadFailure;
            document.head.appendChild(script);
        }

        return () => {
            cancelled = true;
            if (sessionRef.current) {
                sessionRef.current.disconnect();
            }
        };
    }, [call]);

    // Primero la señal 'finished' para que la pantalla del asesor corte al
    // instante (igual polea el estado), después el PATCH. Si la señal falla,
    // el callback se llama igual con el error.
    const submitFinishCallRequest = () => {
        const { storeId, callRequestId } = callRequest;
        const finish = () => finishCallRequest(storeId, callRequestId);
        if (sessionRef.current && sessionRef.current.connection) {
            sessionRef.current.signal(
                { type: 'finished', data: String(callRequestId) },
                finish
            );
        } else {
            finish();
        }
    };

    const handleEnableAudio = () => {
        if (subscriberRef.current) {
            subscriberRef.current.subscribeToAudio(true);
        }
        setAudioBlocked(false);
    };

    const handleCycleVideo = () => {
        if (publisherRef.current) {
            publisherRef.current.cycleVideo()
                .then(() => console.log('::: Camera switched successfully'))
                .catch((error) => {
                    console.error('::: Error switching camera:', error);
                    toastr.error('Error', 'No se pudo cambiar la cámara');
                });
        }
    };

    // Un pago con tarjeta aprobado dentro de la llamada: avisarle al asesor
    // por OpenTok (su pantalla recarga los pedidos al instante, igual que
    // nosotros con su señal 'pin') y refrescar el call request sin esperar al
    // poll de 5 s para que la card pase a "Pagado".
    const handlePaid = purchaseOrderId => {
        if (sessionRef.current) {
            sessionRef.current.signal(
                { type: 'paid', data: String(purchaseOrderId) },
                () => {}
            );
        }
        refreshCallRequestRef.current();
    };

    // El carrito cambió, o el comprador abrió / cerró el checkout: la
    // pantalla del asesor muestra el carrito en vivo y en qué anda la compra
    // ('cart' | 'checkout'); igual polea por si la señal se pierde.
    const signalAdvisor = useCallback((type) => {
        if (sessionRef.current && sessionRef.current.connection) {
            sessionRef.current.signal(
                { type, data: String(Date.now()) },
                () => {}
            );
        }
    }, []);
    const handleCartChanged = useCallback(
        () => signalAdvisor('cart'),
        [signalAdvisor]
    );

    // De vuelta en esta pestaña (p. ej. después de pagar con MercadoPago en
    // otra): refrescar ya, por si el pago se acreditó mientras tanto.
    useEffect(() => {
        const onVisible = () => {
            if (document.visibilityState === 'visible') {
                refreshCallRequestRef.current();
            }
        };
        document.addEventListener('visibilitychange', onVisible);
        window.addEventListener('focus', onVisible);
        return () => {
            document.removeEventListener('visibilitychange', onVisible);
            window.removeEventListener('focus', onVisible);
        };
    }, []);

    const renderPurchaseOrders = purchaseOrders => {
        return (
            <Grid container spacing={2}>
                {purchaseOrders.map((purchaseOrder) => (
                    <Grid size={12} key={purchaseOrder.purchaseOrderId}>
                        <PurchaseOrder
                            purchaseOrder={purchaseOrder}
                            inCall
                            storeName={store && store.name}
                            onPaid={handlePaid}
                        />
                    </Grid>
                ))}
            </Grid>
        );
    };

    return (
        <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column', position: 'relative' }}>
            {/* Video container */}
            <Box sx={{ 
                flex: 1, 
                position: 'relative',
                backgroundColor: '#000',
                overflow: 'hidden'
            }}>
                {/* Subscriber div - remote participant */}
                <Box
                    id="subscriber"
                    // sx={{
                    //     position: 'absolute',
                    //     left: 0,
                    //     top: 0,
                    //     width: '100%',
                    //     height: '100%',
                    //     zIndex: 10,
                    //     // Ensure 9:16 aspect ratio and fill entire height
                    //     aspectRatio: '9/16',
                    //     objectFit: 'cover'
                    // }}
                ></Box>
                
                {/* Publisher div - local participant */}
                <Box
                    id="publisher"
                    // sx={{
                    //     position: 'absolute',
                    //     width: 180, // Smaller size for better proportion
                    //     height: 320, // 9:16 aspect ratio (180 * 16/9 = 320)
                    //     bottom: 20,
                    //     right: 20, // Move to right side
                    //     zIndex: 100,
                    //     border: '3px solid green',
                    //     borderRadius: 1,
                    //     overflow: 'hidden',
                    //     // Ensure 9:16 aspect ratio
                    //     aspectRatio: '9/16'
                    // }}
                />
            </Box>
            
            {/* Audio blocked overlay (browser autoplay policy) */}
            {audioBlocked && (
                <Box sx={{
                    position: 'fixed',
                    top: 20,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    zIndex: 1100
                }}>
                    <Button
                        variant="contained"
                        color="primary"
                        startIcon={<VolumeOffIcon />}
                        onClick={handleEnableAudio}
                    >
                        Activar audio
                    </Button>
                </Box>
            )}

            {/* Lo que la tienda está mostrando (producto pineado o link) */}
            <PinnedSpotlight
                pinnedProduct={pinnedProduct}
                pinnedUrl={pinnedUrl}
                onOpenProduct={setSelectedProduct}
                sx={{
                    position: 'fixed',
                    top: audioBlocked ? 72 : 16,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    width: 'calc(100% - 24px)',
                    maxWidth: 420,
                    zIndex: 1000
                }}
            />

            {/* Catálogo de la tienda, sobre los botones */}
            {catalog.length > 0 && (
                <Box sx={{
                    position: 'fixed',
                    bottom: 88,
                    left: 0,
                    right: 0,
                    zIndex: 1000,
                    display: 'flex',
                    justifyContent: 'center',
                    pointerEvents: 'none'
                }}>
                    <ProductCarousel
                        products={catalog}
                        pinnedProductId={pinnedProduct && pinnedProduct.productId}
                        onSelect={setSelectedProduct}
                        sx={{ pointerEvents: 'auto', maxWidth: 720, width: '100%' }}
                    />
                </Box>
            )}

            {/* QR-detected product button */}
            <QRProductButton
                url={qrUrl}
                onDismiss={dismissQrUrl}
                sx={{ position: 'fixed', bottom: catalog.length > 0 ? 250 : 96, zIndex: 1050 }}
            />

            {/* Floating action buttons */}
            <Box sx={{
                position: 'fixed',
                bottom: 20,
                left: '50%',
                transform: 'translateX(-50%)',
                display: 'flex',
                gap: 2,
                zIndex: 1000
            }}>
                <Tooltip title="Finalizar llamada" placement="top">
                    <Fab
                        color="error"
                        onClick={submitFinishCallRequest}
                        sx={{
                            width: 56,
                            height: 56
                        }}
                    >
                        <CallEndIcon />
                    </Fab>
                </Tooltip>
                
                <Tooltip title="Cambiar cámara" placement="top">
                    <Fab
                        color="default"
                        onClick={handleCycleVideo}
                        sx={{
                            width: 56,
                            height: 56
                        }}
                    >
                        <CameraswitchIcon />
                    </Fab>
                </Tooltip>

                <InCallCart
                    storeId={storeId}
                    extraCount={purchaseOrderCount}
                    tooltip="Ver tu carrito y tus pedidos"
                    callRequestId={callRequest && callRequest.callRequestId}
                    onPaid={handlePaid}
                    onCartChanged={handleCartChanged}
                    onCheckoutOpened={() => signalAdvisor('checkout')}
                    onCheckoutClosed={() => {
                        signalAdvisor('cart');
                        refreshCallRequestRef.current();
                    }}
                >
                    {purchaseOrderCount > 0 && (
                        <Box sx={{ mt: 4 }}>
                            <Typography
                                sx={{ fontWeight: 700, fontSize: '1.1rem' }}
                            >
                                Tus pedidos de esta llamada
                            </Typography>
                            <Typography
                                color="text.secondary"
                                sx={{ fontSize: '0.9rem', mb: 2 }}
                            >
                                Con el estado del pago · los pendientes se pagan acá
                            </Typography>
                            {renderPurchaseOrders(callRequest.purchaseOrders)}
                        </Box>
                    )}
                </InCallCart>
            </Box>

            <ProductQuickDialog
                open={!!selectedProduct}
                onClose={() => setSelectedProduct(null)}
                storeId={storeId}
                product={selectedProduct}
                origin="call"
            />

        </Box>
    );
};

const mapStateToProps = (state) => {
    const callRequest = state.callRequest.callRequest;
    return {
        callRequest,
        call: state.call.call,
        // Para el aviso de "vas a ir a Mercado Pago": el call request no trae
        // la tienda, el catálogo de tiendas sí (mismo lookup que WaitingRoom).
        store: callRequest ? state.stores.stores[callRequest.storeId] : null,
    };
};

export default connect(
    mapStateToProps,
    {
        cancelCallRequestSuccess,
        finishCallRequestSuccess,
        finishCallRequest,
        refreshCallRequest
    }
)(Call);
